import createTransactionsAPI from "@/helpers/rtk_transactions_resource_factory";
import { getCurrentUser } from "@/helpers/session_helper";

const baseURL = process.env.REACT_APP_GLOBO_API + "/transactions/orderRequests";
const type = "OrderRequests";
const currentUser = getCurrentUser();

export const orderRequestsAPI = createTransactionsAPI({
  baseURL: baseURL,
  resource: "OrderRequests",
  tagTypes: ["OrderRequests"],
  idKey: "orderRequestId",
  lookupMap: ({ orderRequestId, referenceNo }) => ({
    value: orderRequestId,
    label: referenceNo,
  }),
  customQuery: (query, { idKey, resource, baseURL }) => ({
    getListOrderRequestsForViewers: query(`${baseURL}/listForViewer`, {
      serializeQueryArgs: ({ endpointName }) => endpointName,
      providesTags: (result) => {
        if (!result?.success || !Array.isArray(result.returnData)) {
          return [{ type: resource, id: "LIST" }];
        }

        return [
          ...result.returnData.map((item) => ({
            type: resource,
            id: item[idKey],
          })),
          { type: resource, id: "LIST" },
        ];
      },
    }),
    getFilesOrderRequests: query(`${baseURL}/files/list`, {
      serializeQueryArgs: ({ endpointName }) => endpointName,
      providesTags: (result) =>
        result?.success
          ? [...(result?.items || result?.returnData)?.map((item) => ({ type: type, id: item[idKey] })), { type: type, id: type }]
          : [{ type: type, id: type }],
    }),
    getTotalsOrderRequests: query(`${baseURL}/getTotals`, {
      serializeQueryArgs: ({ endpointName }) => endpointName,
      providesTags: (result) =>
        result?.success
          ? [...(result?.items || result?.returnData)?.map((item) => ({ type: type, id: item[idKey] })), { type: type, id: type }]
          : [{ type: type, id: type }],
    }),
    getListPendingOrderRequests: query(`${baseURL}/listPendingOrders`, {
      serializeQueryArgs: ({ endpointName }) => endpointName,
      providesTags: (result) =>
        result?.success
          ? [...(result?.items || result?.returnData)?.map((item) => ({ type: type, id: item[idKey] })), { type: type, id: type }]
          : [{ type: type, id: type }],
    }),
    getListPendingDeliveryOrderRequests: query(`${baseURL}/listPendingDelivery`, {
      serializeQueryArgs: ({ endpointName }) => endpointName,
      providesTags: (result) =>
        result?.success
          ? [...(result?.items || result?.returnData)?.map((item) => ({ type: type, id: item[idKey] })), { type: type, id: type }]
          : [{ type: type, id: type }],
    }),
    getListForDeliveryTodayOrderRequests: query(`${baseURL}/listForDeliveryToday`, {
      serializeQueryArgs: ({ endpointName }) => endpointName,
      providesTags: (result) =>
        result?.success
          ? [...(result?.items || result?.returnData)?.map((item) => ({ type: type, id: item[idKey] })), { type: type, id: type }]
          : [{ type: type, id: type }],
    }),
  }),
  customMutations: (mutation, mutationOptions, builder) => ({
    submitOrderRequests: mutation(`${baseURL}/submit`, mutationOptions("save")),
    returnOrderRequests: mutation(`${baseURL}/return`, mutationOptions("save")),
    approveOrderRequests: mutation(`${baseURL}/approve`, mutationOptions("save")),
    receiveOrderRequests: mutation(`${baseURL}/receive`, mutationOptions("save")),
    rejectOrderRequests: mutation(`${baseURL}/reject`, mutationOptions("save")),
    cancelOrderRequests: mutation(`${baseURL}/cancel`, mutationOptions("save")),
    filesUploadOrderRequests: mutation(`${baseURL}/files/upload`),
    filesDeleteOrderRequests: mutation(`${baseURL}/files/delete`),

    exportListForViewerOrderRequests: builder.mutation({
      query: (params) => ({
        url: `${baseURL}/exportListForViewer`,
        method: "POST",
        body: params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentUser?.token}`,
        },
      }),
      async onQueryStarted(params, { queryFulfilled }) {
        try {
          const response = await fetch(`${baseURL}/exportListForViewer`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${currentUser?.token}`,
            },
            body: JSON.stringify(params),
          });

          if (!response.ok) throw new Error("Export failed");

          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `OrderRequests-Viewer-${new Date().toISOString().split("T")[0]}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          await queryFulfilled.catch(() => {});
        } catch (err) {
          console.error("Export failed:", err);
          throw err;
        }
      },
    }),
    exportListPendingOrderRequests: builder.mutation({
      query: (params) => ({
        url: `${baseURL}/exportListPendingOrders`,
        method: "POST",
        body: params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentUser?.token}`,
        },
      }),
      async onQueryStarted(params, { queryFulfilled }) {
        try {
          const response = await fetch(`${baseURL}/exportListPendingOrders`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${currentUser?.token}`,
            },
            body: JSON.stringify(params),
          });

          if (!response.ok) throw new Error("Export failed");

          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `OrderRequests-Pending-${new Date().toISOString().split("T")[0]}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          await queryFulfilled.catch(() => {});
        } catch (err) {
          console.error("Export failed:", err);
          throw err;
        }
      },
    }),
    exportListPendingDeliveryOrderRequests: builder.mutation({
      query: (params) => ({
        url: `${baseURL}/exportListPendingDelivery`,
        method: "POST",
        body: params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentUser?.token}`,
        },
      }),
      async onQueryStarted(params, { queryFulfilled }) {
        try {
          const response = await fetch(`${baseURL}/exportListPendingDelivery`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${currentUser?.token}`,
            },
            body: JSON.stringify(params),
          });

          if (!response.ok) throw new Error("Export failed");

          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `OrderRequests-PendingDelivery-${new Date().toISOString().split("T")[0]}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          await queryFulfilled.catch(() => {});
        } catch (err) {
          console.error("Export failed:", err);
          throw err;
        }
      },
    }),
    exportListForDeliveryTodayOrderRequests: builder.mutation({
      query: (params) => ({
        url: `${baseURL}/exportListForDeliveryToday`,
        method: "POST",
        body: params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentUser?.token}`,
        },
      }),
      async onQueryStarted(params, { queryFulfilled }) {
        try {
          const response = await fetch(`${baseURL}/exportListForDeliveryToday`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${currentUser?.token}`,
            },
            body: JSON.stringify(params),
          });

          if (!response.ok) throw new Error("Export failed");

          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `OrderRequests-ForDeliveryToday-${new Date().toISOString().split("T")[0]}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          await queryFulfilled.catch(() => {});
        } catch (err) {
          console.error("Export failed:", err);
          throw err;
        }
      },
    }),
  }),
});

export const {
  useGetOrderRequestsQuery,
  useGetListOrderRequestsForViewersQuery,
  useExportOrderRequestsMutation,
  useFindOrderRequestsQuery,
  useLookUpOrderRequestsMutation,
  useSaveOrderRequestsMutation,
  useSubmitOrderRequestsMutation,
  useReturnOrderRequestsMutation,
  useApproveOrderRequestsMutation,
  useReceiveOrderRequestsMutation,
  useRejectOrderRequestsMutation,
  useCancelOrderRequestsMutation,
  useDeleteOrderRequestsMutation,
  useFilesUploadOrderRequestsMutation,
  useFilesDeleteOrderRequestsMutation,
  useGetFilesOrderRequestsQuery,
  useExportListForViewerOrderRequestsMutation,
  useGetTotalsOrderRequestsQuery,
  useGetListPendingOrderRequestsQuery,
  useGetListPendingDeliveryOrderRequestsQuery,
  useGetListForDeliveryTodayOrderRequestsQuery,
  useExportListPendingOrderRequestsMutation,
  useExportListPendingDeliveryOrderRequestsMutation,
  useExportListForDeliveryTodayOrderRequestsMutation,
} = orderRequestsAPI;
